import { LocalVideoTrack } from 'twilio-video';
import { useCallback, useRef, useState } from 'react';
import useVideoContext from '../useVideoContext/useVideoContext';

export default function useLocalVideoToggle() {
	const {
		room,
		localTracks,
		getLocalVideoTrack,
		removeLocalVideoTrack,
		discardLocalTrack,
		onError,
	} = useVideoContext();
	const localParticipant = room?.localParticipant;
	const videoTrack = localTracks.find(
		(track) => !track.name.includes('screen') && track.kind === 'video',
	) as LocalVideoTrack;

	const hasLiveVideo = Boolean(
		videoTrack &&
		!videoTrack.isStopped &&
		videoTrack.mediaStreamTrack?.readyState !== 'ended',
	);
	const [isPublishing, setIsPublishing] = useState(false);
	const pending = useRef(false);
	const toggleVideoEnabled = useCallback(() => {
		if (!pending.current && !isPublishing) {
			if (hasLiveVideo) {
				const localTrackPublication =
					localParticipant?.unpublishTrack(videoTrack);
				// TODO: remove when SDK implements this event. See: https://issues.corp.twilio.com/browse/JSDK-2592
				localParticipant?.emit(
					'trackUnpublished',
					localTrackPublication,
				);
				removeLocalVideoTrack();
			} else {
				if (videoTrack) discardLocalTrack(videoTrack);
				pending.current = true;
				let acquired: LocalVideoTrack | undefined;
				setIsPublishing(true);
				getLocalVideoTrack()
					.then((track: LocalVideoTrack) => {
						acquired = track;
						if (track.isStopped || room?.state === 'disconnected') {
							track.stop();
							return;
						}
						return localParticipant?.publishTrack(track, {
							priority: 'low',
						});
					})
					.catch((error) => {
						if (acquired) discardLocalTrack(acquired);
						if (
							room?.state !== 'disconnected' &&
							!/cancelled/i.test(error.message)
						)
							onError(error);
					})
					.finally(() => {
						pending.current = false;
						setIsPublishing(false);
					});
			}
		}
	}, [
		isPublishing,
		hasLiveVideo,
		room,
		videoTrack,
		localParticipant,
		getLocalVideoTrack,
		onError,
		removeLocalVideoTrack,
		discardLocalTrack,
	]);

	return [hasLiveVideo, toggleVideoEnabled] as const;
}
